import Header from "../components/Header";
import { useEffect, useRef, useState } from "react";
import {useQueryClient} from "react-query";
import {useAuth} from "../authentication/AuthContext";
import {fetchSettings, updateSettings} from "./api/settingsApi";
import {
    clampBreakDurationMinutes,
    DEFAULT_BREAK_DURATION_MINUTES,
    MAX_BREAK_DURATION_MINUTES,
    MIN_BREAK_DURATION_MINUTES,
    readBreakDurationMinutes,
    DEFAULT_POMODORO_SCORE,
    MIN_POMODORO_SCORE,
    MAX_POMODORO_SCORE,
    DEFAULT_REVIEWED_NOTE_SCORE,
    DEFAULT_REVIEWED_CARD_SCORE,
    MIN_PRODUCTIVITY_SCORE,
    MAX_PRODUCTIVITY_SCORE,
    clampProductivityScore,
} from "./settingsPreferences";
import {applyTheme, getStoredTheme, THEME_STORAGE_KEY} from "../theme/theme";

function Settings() {
    const queryClient = useQueryClient();
    const [cycles, setCycles] = useState(25);
    const [breakDuration, setBreakDuration] = useState(DEFAULT_BREAK_DURATION_MINUTES);
    const [pomodoroScore, setPomodoroScore] = useState(DEFAULT_POMODORO_SCORE);
    const [reviewedNoteScore, setReviewedNoteScore] = useState(DEFAULT_REVIEWED_NOTE_SCORE);
    const [reviewedCardScore, setReviewedCardScore] = useState(DEFAULT_REVIEWED_CARD_SCORE);
    const [breakTasks, setBreakTasks] = useState([]);
    const [newBreakTask, setNewBreakTask] = useState("");
    const [theme, setTheme] = useState(getStoredTheme);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState("");
    const lastSavedSettingsRef = useRef("");
    const saveQueueRef = useRef(Promise.resolve());
    const pendingSavesRef = useRef(0);
    const {authToken, user} = useAuth();
    const username = user?.displayName || user?.email || "—";
    const email = user?.email || "—";

    useEffect(() => {
        let isMounted = true;

        async function loadSettings() {
            if (!authToken) {
                return;
            }

            setIsLoading(true);
            setError("");

            try {
                const data = await fetchSettings(authToken);
                const nextCycles = Number(data?.pomodoro_cycle_duration ?? data?.pomodoroCycleDuration ?? 25);
                const nextBreakDuration = readBreakDurationMinutes(data);
                const nextTheme = data?.appearance === "light" ? "light" : "dark";
                const nextPomodoroScore = Math.min(MAX_POMODORO_SCORE, Math.max(MIN_POMODORO_SCORE, Number(data?.pomodoro_score ?? data?.pomodoroScore ?? DEFAULT_POMODORO_SCORE)));
                const nextReviewedNoteScore = clampProductivityScore(data?.reviewed_note_score ?? data?.reviewedNoteScore, DEFAULT_REVIEWED_NOTE_SCORE);
                const nextReviewedCardScore = clampProductivityScore(data?.reviewed_card_score ?? data?.reviewedCardScore, DEFAULT_REVIEWED_CARD_SCORE);
                const nextBreakTasks = Array.isArray(data?.break_tasks ?? data?.breakTasks)
                    ? (data?.break_tasks ?? data?.breakTasks).filter((t) => typeof t === "string")
                    : [];

                if (!isMounted) {
                    return;
                }

                setCycles(Number.isFinite(nextCycles) && nextCycles > 0 ? Math.trunc(nextCycles) : 25);
                setBreakDuration(nextBreakDuration);
                setTheme(nextTheme);
                setPomodoroScore(Number.isFinite(nextPomodoroScore) ? Math.trunc(nextPomodoroScore) : DEFAULT_POMODORO_SCORE);
                setReviewedNoteScore(nextReviewedNoteScore);
                setReviewedCardScore(nextReviewedCardScore);
                setBreakTasks(nextBreakTasks);
                localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
                lastSavedSettingsRef.current = JSON.stringify({
                    pomodoro_cycle_duration: Number.isFinite(nextCycles) && nextCycles > 0 ? Math.trunc(nextCycles) : 25,
                    break_duration: nextBreakDuration,
                    appearance: nextTheme,
                    pomodoro_score: Number.isFinite(nextPomodoroScore) ? Math.trunc(nextPomodoroScore) : DEFAULT_POMODORO_SCORE,
                    reviewed_note_score: nextReviewedNoteScore,
                    reviewed_card_score: nextReviewedCardScore,
                    break_tasks: nextBreakTasks,
                });
            } catch (fetchError) {
                if (!isMounted) {
                    return;
                }

                setError(fetchError instanceof Error ? fetchError.message : "Failed to load settings");
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadSettings();

        return () => {
            isMounted = false;
        };
    }, [authToken]);

    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    useEffect(() => {
        if (isLoading || !authToken) {
            return undefined;
        }

        const settings = {
            pomodoro_cycle_duration: Math.max(1, Math.trunc(cycles)),
            break_duration: clampBreakDurationMinutes(breakDuration),
            appearance: theme,
            pomodoro_score: Math.min(MAX_POMODORO_SCORE, Math.max(MIN_POMODORO_SCORE, Math.trunc(pomodoroScore))),
            reviewed_note_score: clampProductivityScore(reviewedNoteScore, DEFAULT_REVIEWED_NOTE_SCORE),
            reviewed_card_score: clampProductivityScore(reviewedCardScore, DEFAULT_REVIEWED_CARD_SCORE),
            break_tasks: breakTasks,
        };
        const serializedSettings = JSON.stringify(settings);

        if (serializedSettings === lastSavedSettingsRef.current) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            pendingSavesRef.current += 1;
            setIsSaving(true);
            setError("");

            saveQueueRef.current = saveQueueRef.current
                .catch(() => undefined)
                .then(() => updateSettings(authToken, settings))
                .then(async () => {
                    lastSavedSettingsRef.current = serializedSettings;
                    await queryClient.invalidateQueries(["stats"]);
                    localStorage.setItem(THEME_STORAGE_KEY, settings.appearance);
                    setSavedAt(new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }));
                })
                .catch((saveError) => {
                    setError(saveError instanceof Error ? saveError.message : "Failed to save settings");
                })
                .finally(() => {
                    pendingSavesRef.current -= 1;
                    if (pendingSavesRef.current === 0) {
                        setIsSaving(false);
                    }
                });
        }, 400);

        return () => window.clearTimeout(timeoutId);
    }, [authToken, breakDuration, breakTasks, cycles, isLoading, pomodoroScore, queryClient, reviewedCardScore, reviewedNoteScore, theme]);

    return (
        <div className="min-h-screen bg-[#12131a] text-gray-100">
            <Header/>
            <main className="mx-auto w-full max-w-3xl px-5 pb-16 pt-24">
                <div className="mb-8">
                    <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
                    <p className="mt-1 text-sm text-gray-400">Personalize your workspace and save preferences to your account.</p>
                </div>

                <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
                    {error ? (
                        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                            {error}
                        </div>
                    ) : null}

                    <section className="rounded-2xl border border-[#30384D] bg-gradient-to-b from-[#222831] to-[#1D222D] p-5 shadow-[0_16px_32px_rgba(0,0,0,0.25)]">
                        <h2 className="text-base font-semibold">Pomodoro</h2>
                        <p className="mt-1 text-sm text-gray-400">Set your default focus and break durations in minutes.</p>

                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            <label className="block">
                                <span className="mb-1 block text-xs text-gray-400">Focus duration</span>
                                <input
                                    type="number"
                                    className="w-full rounded-md border border-[#4B556B] bg-[#161B25] p-2 text-sm text-gray-100 outline-none focus:border-indigo-500"
                                    min={1}
                                    step={1}
                                    value={cycles}
                                    onChange={(e) => setCycles(Math.max(1, Math.trunc(Number(e.target.value) || 1)))}
                                    disabled={isLoading}
                                />
                            </label>

                            <label className="block">
                                <span className="mb-1 block text-xs text-gray-400">Break duration</span>
                                <input
                                    type="number"
                                    className="w-full rounded-md border border-[#4B556B] bg-[#161B25] p-2 text-sm text-gray-100 outline-none focus:border-indigo-500"
                                    min={MIN_BREAK_DURATION_MINUTES}
                                    max={MAX_BREAK_DURATION_MINUTES}
                                    step={1}
                                    value={breakDuration}
                                    onChange={(e) => setBreakDuration(clampBreakDurationMinutes(e.target.value))}
                                    disabled={isLoading}
                                />
                            </label>
                        </div>

                        <div className="mt-6 border-t border-[#30384D] pt-5">
                            <h3 className="text-sm font-semibold">Break Tasks</h3>
                            <p className="mt-1 text-sm text-gray-400">Tasks to remind yourself to do during breaks.</p>

                            <ul className="mt-4 space-y-2">
                                {breakTasks.map((task, index) => (
                                    <li key={index} className="flex items-center gap-2">
                                        <span className="flex-1 rounded-md border border-[#4B556B] bg-[#161B25] px-3 py-2 text-sm text-gray-100">
                                            {task}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={isLoading}
                                            onClick={() => setBreakTasks((prev) => prev.filter((_, i) => i !== index))}
                                            className="rounded-md border border-[#4B556B] bg-[#2B3242] px-3 py-2 text-sm text-gray-400 hover:text-red-400 transition"
                                        >
                                            ✕
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-3 flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Add a break task..."
                                    value={newBreakTask}
                                    onChange={(e) => setNewBreakTask(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            const trimmed = newBreakTask.trim();
                                            if (trimmed) {
                                                setBreakTasks((prev) => [...prev, trimmed]);
                                                setNewBreakTask("");
                                            }
                                        }
                                    }}
                                    disabled={isLoading}
                                    className="flex-1 rounded-md border border-[#4B556B] bg-[#161B25] p-2 text-sm text-gray-100 outline-none focus:border-indigo-500 placeholder-gray-500"
                                />
                                <button
                                    type="button"
                                    disabled={isLoading || !newBreakTask.trim()}
                                    onClick={() => {
                                        const trimmed = newBreakTask.trim();
                                        if (trimmed) {
                                            setBreakTasks((prev) => [...prev, trimmed]);
                                            setNewBreakTask("");
                                        }
                                    }}
                                    className="rounded-md border border-[#4B556B] bg-[#2B3242] px-3 py-2 text-sm font-medium disabled:opacity-40"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-2xl border border-[#30384D] bg-gradient-to-b from-[#222831] to-[#1D222D] p-5 shadow-[0_16px_32px_rgba(0,0,0,0.25)]">
                        <h2 className="text-base font-semibold">Productivity Score</h2>
                        <p className="mt-1 text-sm text-gray-400">Choose how many points each completed focus or review action earns.</p>

                        <div className="mt-4 grid gap-4 sm:grid-cols-3">
                            {[
                                ["Score per completed pomodoro", pomodoroScore, setPomodoroScore],
                                ["Score for reviewed note", reviewedNoteScore, setReviewedNoteScore],
                                ["Score for each reviewed card", reviewedCardScore, setReviewedCardScore],
                            ].map(([label, value, setter]) => (
                                <label className="block" key={label}>
                                    <span className="mb-1 block text-xs text-gray-400">{label}</span>
                                    <input type="number" className="w-full min-w-0 rounded-md border border-[#4B556B] bg-[#161B25] p-2 text-sm text-gray-100 outline-none focus:border-indigo-500" min={MIN_PRODUCTIVITY_SCORE} max={MAX_PRODUCTIVITY_SCORE} step={1} value={value} onChange={(event) => setter(clampProductivityScore(event.target.value, MIN_PRODUCTIVITY_SCORE))} disabled={isLoading}/>
                                </label>
                            ))}
                        </div>
                    </section>

                    <section className="rounded-2xl border border-[#30384D] bg-gradient-to-b from-[#222831] to-[#1D222D] p-5 shadow-[0_16px_32px_rgba(0,0,0,0.25)]">
                        <h2 className="text-base font-semibold">Appearance</h2>
                        <p className="mt-1 text-sm text-gray-400">Choose between dark and light theme preference.</p>

                        <div className="mt-4 inline-flex rounded-xl border border-[#4B556B] bg-[#161B25] p-1">
                            <button
                                type="button"
                                onClick={() => setTheme("dark")}
                                disabled={isLoading}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${theme === "dark" ? "bg-indigo-600 text-white" : "text-gray-300 hover:bg-[#202636]"}`}
                            >
                                Dark
                            </button>
                            <button
                                type="button"
                                onClick={() => setTheme("light")}
                                disabled={isLoading}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${theme === "light" ? "bg-indigo-600 text-white" : "text-gray-300 hover:bg-[#202636]"}`}
                            >
                                Light
                            </button>
                        </div>
                    </section>

                    <section className="rounded-2xl border border-[#30384D] bg-gradient-to-b from-[#222831] to-[#1D222D] p-5 shadow-[0_16px_32px_rgba(0,0,0,0.25)]">
                        <h2 className="text-base font-semibold">User Account</h2>
                        <p className="mt-1 text-sm text-gray-400">Username and email are shown here for reference.</p>

                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            <label className="block">
                                <span className="mb-1 block text-xs text-gray-400">Username</span>
                                <input
                                    type="text"
                                    value={username}
                                    readOnly
                                    disabled
                                    className="w-full rounded-md border border-[#4B556B] bg-[#161B25] p-2 text-sm text-gray-300 opacity-80"
                                />
                            </label>

                            <label className="block">
                                <span className="mb-1 block text-xs text-gray-400">Email</span>
                                <input
                                    type="text"
                                    value={email}
                                    readOnly
                                    disabled
                                    className="w-full rounded-md border border-[#4B556B] bg-[#161B25] p-2 text-sm text-gray-300 opacity-80"
                                />
                            </label>
                        </div>
                    </section>

                    <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">
                            {isLoading
                                ? "Loading settings..."
                                : isSaving
                                    ? "Saving..."
                                    : savedAt
                                        ? `Saved at ${savedAt}`
                                        : "Changes are saved automatically."}
                        </span>
                    </div>
                </form>
            </main>
        </div>
    )
}

export default Settings;
